import { createFileRoute, Navigate, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { BOARD_STATES } from '@/features/hr-attendance-board/api'
import { hasGrant, meQuery } from '@/lib/me'
import { canSeeWith } from '@/lib/rail-visibility'

const attendanceBoardSearchSchema = z.object({
  date: z.string().optional(),
  department_id: z.string().optional(),
  schedule_id: z.string().optional(),
  search: z.string().optional(),
  // The board's own list, imported rather than repeated, so a new state
  // cannot be filterable on the screen and rejected by the URL.
  state: z.enum(BOARD_STATES).optional(),
  sort: z.enum(['name', 'department', 'state', 'lateness']).default('name'),
  direction: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(200).default(25),
})

export const Route = createFileRoute('/_authenticated/hr/attendance-board')({
  validateSearch: attendanceBoardSearchSchema,
  beforeLoad: ({ context }) => {
    const me = context.queryClient.getQueryData(meQuery.queryKey)
    if (
      !hasGrant(context.queryClient, 'attendance.view') ||
      !canSeeWith(me as Parameters<typeof canSeeWith>[0], {
        permission: 'attendance.view',
        minScope: 'own',
      })
    ) {
      throw redirect({ to: '/hr' })
    }
  },
  component: AttendanceBoardRoute,
})

/**
 * The daily board is the attendance screen's Day view now (D10). This path
 * keeps its guard and only forwards an old link, its date and filters with it.
 */
function AttendanceBoardRoute() {
  const { date, department_id, schedule_id, search, state } = Route.useSearch()

  return (
    <Navigate
      to="/hr/attendance"
      search={{
        ...(date ? { from: date, to: date } : {}),
        department_id,
        schedule_id,
        search,
        state,
      }}
      replace
    />
  )
}
